/**
 * Overlay Value Normalization
 *
 * Templates store overlay values in mixed formats (floats 0-1, strings
 * like "low"/"medium"/"high"). The renderers (drawLineArt, applyGrain)
 * expect integers 1-100. This module bridges the gap.
 */

const DENSITY_MAP = { low: 25, medium: 50, high: 75 };

/**
 * Normalize an overlay value to an integer in the 1-100 range.
 *
 * Handles:
 *   - Named strings: "low" → 25, "medium" → 50, "high" → 75
 *   - Floats 0-1: 0.25 → 25
 *   - Integers 1-100: pass through
 *   - Falsy/missing: returns defaultVal
 *
 * @param {string|number|undefined} value
 * @param {number} defaultVal - fallback if value is missing or unrecognized
 * @returns {number} Integer 1-100
 */
export function normalizeOverlay(value, defaultVal) {
  if (value == null) return defaultVal;

  if (typeof value === 'string') {
    return DENSITY_MAP[value] || defaultVal;
  }

  if (typeof value === 'number') {
    // Distinguish floats (0-1 range) from integers (1-100).
    // Only treat as a float if the value is strictly fractional (has a decimal part)
    // AND is in the 0-1 range. Integer 1 means "1%" not "100%".
    // Edge case: 0 → 0 (not defaultVal — sliders need true zero to work)
    if (value === 0) return 0;
    if (value > 0 && value < 1 && value % 1 !== 0) {
      return Math.round(value * 100);
    }
    // Integer or value > 1 → pass through, clamped to 1-100
    return Math.max(0, Math.min(100, Math.round(value))) || defaultVal;
  }

  return defaultVal;
}
